import 'react-phone-input-2/lib/style.css'

import { parsePhoneNumberFromString } from 'libphonenumber-js'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import PhoneInput from 'react-phone-input-2'

import useForm from '../../../../hooks/useForm'
import ConfirmationDialog from '../../ConfirmationDialog/index'
import Input from '../../Input/index'
import { ReactComponent as GroupIcon } from './group.svg'
import { ReactComponent as InfoIcon } from './info.svg'
import { ReactComponent as MessagingIcon } from './messaging.svg'
import styles from './MessagingSetupDialog.module.scss'

const MessagingSetupDialog = ({
  didMessagingStarted = false,
  isGroupBooking = false,
  guestFirstName = '',
  guestLastName = '',
  phoneNumber = '+1',
  roomNumber = '',
  handleConfirm,
  handleCancel
}) => {
  const [guestPhoneCountryCode, setGuestPhoneCountryCode] = useState()
  const [guestPhoneNumber, setGuestPhoneNumber] = useState()

  useEffect(() => {
    const parsedNumber =
      typeof phoneNumber === 'string' && parsePhoneNumberFromString(phoneNumber)

    if (parsedNumber) {
      const { countryCallingCode, nationalNumber } = parsedNumber

      setGuestPhoneNumber(nationalNumber)
      setGuestPhoneCountryCode(countryCallingCode)
    }
  }, [phoneNumber])

  const [values, setValues, setValue] = useForm({
    guestFirstName,
    guestLastName,
    phoneNumber,
    roomNumber
  })

  const [isFormValidated, setFormValidated] = useState(false)

  const phoneLabelRef = useRef()

  useEffect(() => {
    if (
      phoneLabelRef.current &&
      phoneLabelRef.current.querySelector('input') &&
      process.env.JEST_WORKER_ID === undefined
    ) {
      phoneLabelRef.current.querySelector('input').focus()
    }
  }, [phoneLabelRef])

  useEffect(() => {
    const firstNamePass = values.guestFirstName && values.guestFirstName.length > 1
    const lastNamePass = values.guestLastName && values.guestLastName.length > 1
    const phoneNumberPass =
      values.phoneNumber && values.phoneNumber.replace(/[^0-9]+/g, '').length > 6
    const roomNumberPass = values.roomNumber && values.roomNumber.length > 1

    if (firstNamePass && lastNamePass && phoneNumberPass && roomNumberPass) {
      setFormValidated(true)
    } else {
      setFormValidated(false)
    }
  }, [
    isGroupBooking,
    values.guestFirstName,
    values.guestLastName,
    values.phoneNumber,
    values.roomNumber
  ])

  function handleChangePhone(phoneNumber) {
    setValue({ phoneNumber })

    const parsedNumber =
      typeof phoneNumber === 'string' && parsePhoneNumberFromString(phoneNumber)

    if (parsedNumber) {
      const { countryCallingCode, nationalNumber } = parsedNumber
      setGuestPhoneCountryCode(countryCallingCode)
      setGuestPhoneNumber(nationalNumber)
    }
  }

  return (
    <ConfirmationDialog
      positiveMessage={didMessagingStarted ? 'Update guest info' : 'Enable'}
      negativeMessage="Cancel"
      onConfirm={() =>
        handleConfirm({
          guestFirstName: values.guestFirstName,
          guestLastName: values.guestLastName,
          roomNumber: values.roomNumber,
          guestPhoneNumber: guestPhoneNumber || values.phoneNumber,
          guestPhoneCountryCode: guestPhoneCountryCode || '1'
        })
      }
      onClose={handleCancel}
      isButtonDisabled={!isFormValidated}
      title="Set up messaging"
      hasFixedTopMargin={true}
    >
      <div className={styles.container}>
        <div className={styles.bar}>
          {!isGroupBooking && !didMessagingStarted && (
            <>
              <InfoIcon />
              Guest will get a welcome text after enabling
            </>
          )}
          {isGroupBooking && (
            <>
              <GroupIcon />
              For group booking of {guestFirstName} {guestLastName}
            </>
          )}
          {didMessagingStarted && (
            <>
              <MessagingIcon />
              Messaging already started
            </>
          )}
        </div>
        <div className={styles.phoneLabelContainer}>
          <label className={styles.phoneLabel} ref={phoneLabelRef}>
            <span className={styles.labelInner}>Cell phone number</span>
            <PhoneInput
              inputExtraProps={{ autoComplete: 'off' }}
              defaultCountry="US"
              onChange={handleChangePhone}
              value={values.phoneNumber || '+1'}
            />
          </label>
        </div>
        <div className={styles.nameInputs}>
          <Input
            autoComplete="disabled"
            label="First name"
            name="guestFirstName"
            handleChange={setValues}
            value={values.guestFirstName}
          />
          <Input
            autoComplete="disabled"
            label="Last name"
            name="guestLastName"
            handleChange={setValues}
            value={values.guestLastName}
          />
        </div>
        {(isGroupBooking || !roomNumber) && (
          <>
            <Input
              autoComplete="disabled"
              isFullWidth
              label="Room number"
              name="roomNumber"
              handleChange={e =>
                setValue({ roomNumber: e.target.value.replace(/\D/g, '') })
              }
              value={values.roomNumber}
            />
            <button
              style={{ color: '#4079F5', fontSize: 14, fontWeight: 500, marginTop: 3 }}
              onClick={() => setValue({ roomNumber: 'N/A' })}
            >
              No room number
            </button>
          </>
        )}
      </div>
    </ConfirmationDialog>
  )
}

MessagingSetupDialog.propTypes = {
  didMessagingStarted: PropTypes.bool,
  isGroupBooking: PropTypes.bool,
  clientFullName: PropTypes.string,
  guestFirstName: PropTypes.string,
  guestLastName: PropTypes.string,
  phoneNumber: PropTypes.string,
  roomNumber: PropTypes.string,
  handleConfirm: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired
}

export default MessagingSetupDialog
